import React from 'react';

import { useAppSelector } from '@/hooks/redux';

import { selectExistingCustomerConfigs } from '@/redux/configs/selectors';
import { selectUser } from '@/redux/user-data';

import { AccountTypeDetails } from '@/types/api/account';
import { User } from '@/types/user';

import { isCaymanUser, isExistingCustomer } from '@/utils/user';

import { AccountTypeCard } from '../components/AccountTypeCard';
import MaxAccountsPerTypeAlert from '../components/MaxAccountsPerTypeAlert';
import { getAccountsWithCheckingLimit } from '../utils/account';
import CaymanAccountTypeHOC from './CaymanAccountTypeHOC';

export type AccountTypeHOCProps = {
  showCheckbox?: boolean;
  accountTypes: AccountTypeDetails[];
  selectedAccountTypes: AccountTypeDetails[];
  onSeeDetails: (data: AccountTypeDetails, index: number) => void;
  toggleSelect: (data: AccountTypeDetails) => void;
};

const BaseAccountTypeHOC: React.FC<AccountTypeHOCProps> = ({
  accountTypes,
  selectedAccountTypes,
  onSeeDetails,
  toggleSelect,
  showCheckbox = true,
}) => {
  const user = useAppSelector(selectUser) as User;
  const existingCustomerConfigs = useAppSelector(selectExistingCustomerConfigs);

  const accountTypesOptions: AccountTypeDetails[] = accountTypes.map(
    accountType => ({
      ...accountType,
      isEligible: true,
      reachedMaxAccountsPerType: false,
    }),
  );

  if (isCaymanUser(user)) {
    return (
      <CaymanAccountTypeHOC
        user={user}
        accountTypes={accountTypes}
        selectedAccountTypes={selectedAccountTypes}
        onSeeDetails={onSeeDetails}
        toggleSelect={toggleSelect}
      />
    );
  }

  return getAccountsWithCheckingLimit({
    accounts: accountTypesOptions,
    userAccountTypes: user.accountTypes || [],
    selectedAccounts: selectedAccountTypes,
    maxAccountsPerType: isExistingCustomer(user)
      ? existingCustomerConfigs?.maxAccountsPerType
      : -1,
    requiredSort: true,
  }).map((item, index) => (
    <AccountTypeCard
      key={item.id}
      data={item}
      showCheckbox={showCheckbox}
      isDisabled={item.reachedMaxAccountsPerType}
      isSelected={selectedAccountTypes.some(account => account.id === item.id)}
      onSeeDetailsClick={data => onSeeDetails(data, index)}
      onClick={toggleSelect}
      warningMessage={
        item.reachedMaxAccountsPerType ? <MaxAccountsPerTypeAlert /> : null
      }
    />
  ));
};

export default BaseAccountTypeHOC;
